import Cookies from 'js-cookie'
import { prop } from 'ramda'
import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import {
  DOMAIN_NAME,
  dashboardUrl,
  loginUrl,
  logoutUrl,
  SSO_REDIRECT_COOKIE,
  ssoLogoutUrl,
} from 'app/constants'
import { cacheActions } from 'core/caching/cacheReducers'
import { notificationActions } from 'core/notifications/notificationReducers'
import { permissionsActions } from 'core/session/permissionsReducers'
import { preferencesActions } from 'core/session/preferencesReducers'
import { sessionActions, sessionStoreKey } from 'core/session/sessionReducers'
import useScopedPreferences from 'core/session/useScopedPreferences'
import { getAppBasePath, isOpenCloud } from 'core/utils/helpers'
import Bugsnag from 'utils/bugsnag'
import { resetAmplitudeUser, trackEvent } from 'utils/tracking'

// We are abusing the React component system a little bit here.  This is really
// nothing but an action but I didn't want to clutter the Navbar component with
// more code.  This gives us a nice clean separation.
const LogoutPage = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const session = useSelector(prop(sessionStoreKey))
  const { username, isSsoToken, features } = session
  const { updatePrefs } = useScopedPreferences()
  const isOpenCloudSsoUser = isOpenCloud() && isSsoToken
  const infraHost = features?.sso_infra_url

  useEffect(() => {
    const metadata = {
      username,
      du_domain: window.location.origin,
    }
    Bugsnag.leaveBreadcrumb('PF9 Logged Out', metadata)
    trackEvent('PF9 Logged Out', metadata)
    resetAmplitudeUser()

    // Clear the SSO redirect cookie
    Cookies.remove(SSO_REDIRECT_COOKIE)
    Cookies.remove(DOMAIN_NAME)

    // Clear last visited path and intended path on logout
    dispatch(
      preferencesActions?.updatePrefs({
        username,
        key: 'root',
        prefs: { lastVisitedPath: '' },
      }),
    )

    // Clear currentRegion for OpenCloud. We do not want the user to be
    // back in to the same region when they log back in
    if (isOpenCloud()) {
      updatePrefs({ currentRegion: null })
    }

    if (isOpenCloudSsoUser) {
      // Add pendingSsoLogout=true to the URL before the session is destroyed.
      // AppContainer will automatically redirect user to the login page when session is cleared.
      // so we need this param to tell AppContainer to hold off on the redirect. After session is cleared,
      // we will redirect SSO users to SSO global logout endpoint.
      const params = new URLSearchParams(location.search)
      params.set('pendingSsoLogout', 'true')
      history.replace({
        pathname: location.pathname,
        search: params.toString(),
        state: { from: logoutUrl },
      })
    } else {
      // Clear location state to ensure redirect to dashboard after intentional logout
      // instead of previously intended paths
      history.replace(location.pathname, { from: logoutUrl })
    }

    dispatch(sessionActions.destroySession())
    dispatch(cacheActions.clearCache())
    dispatch(notificationActions.clearNotifications())
    dispatch(permissionsActions.clearPermissions())

    if (isOpenCloudSsoUser) {
      // Redirect to SSO global logout endpoint
      const appBasePath = getAppBasePath()
      window.location.replace(`https://${infraHost}${appBasePath}${ssoLogoutUrl}`)
    }
  }, [])

  return <div />
}

export default LogoutPage
